import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import LicenseUpsell from "@/components/LicenseUpsell";
import ProgressRing from "@/components/ProgressRing";
import { ArrowLeft, CheckCircle2, XCircle, Trophy, RotateCcw, ArrowRight } from "lucide-react";

export default function QuizView() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const modQ = useQuery({ queryKey: ["module", moduleId], queryFn: async () => (await api.get(`/course/${moduleId}`)).data, retry: false });

  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const m = modQ.data;

  if (modQ.error?.response?.status === 403) {
    return (
      <div className="min-h-screen bg-background grain">
        <Navbar />
        <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
          <Link to={`/module/${moduleId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Link>
          <LicenseUpsell />
        </main>
      </div>
    );
  }

  if (modQ.isLoading || !m) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-10"><div className="h-96 rounded-2xl glass animate-pulse" /></div>
      </div>
    );
  }

  const allAnswered = m.quiz.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/quiz/${moduleId}/submit`, { answers });
      setResult(r.data);
      await qc.invalidateQueries({ queryKey: ["progress"] });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setAnswers({});
    setResult(null);
    window.scrollTo({ top: 0 });
  };

  const resMap = {};
  if (result) result.results.forEach((r) => (resMap[r.id] = r));

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
        <Link to={`/module/${moduleId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {m.title}
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Trophy className="h-5 w-5 text-gold" />
          <span className="font-mono text-xs text-gold">Modul {String(m.number).padStart(2, "0")} · Quiz</span>
        </div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mb-8">Testen Sie Ihr Wissen</h1>

        {/* Result banner */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl glass-strong p-6 mb-8 flex items-center gap-6 ${result.passed ? "glow-gold" : ""}`}
              data-testid="quiz-result-banner"
            >
              <ProgressRing percent={result.percent} size={88} stroke={8} />
              <div>
                <h2 className="font-heading font-bold text-2xl">
                  {result.passed ? "Bestanden! 🎉" : "Noch nicht bestanden"}
                </h2>
                <p className="text-foreground/70 mt-1">
                  {result.score} von {result.total} richtig. {result.passed ? "Stark – weiter zum nächsten Modul." : "Sie brauchen 70% – schauen Sie sich die Erklärungen an und versuchen Sie es erneut."}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Questions */}
        <div className="space-y-5">
          {m.quiz.map((q, qi) => {
            const r = resMap[q.id];
            return (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: qi * 0.05 }}
                className="rounded-2xl glass p-5 sm:p-6"
                data-testid={`quiz-question-${qi}`}
              >
                <div className="flex gap-3">
                  <span className="flex-shrink-0 h-7 w-7 rounded-full bg-gold/15 border border-gold/30 text-gold text-sm font-mono flex items-center justify-center">{qi + 1}</span>
                  <h3 className="font-medium text-lg leading-snug">{q.q}</h3>
                </div>
                <div className="mt-4 space-y-2.5 sm:pl-10">
                  {q.options.map((opt, oi) => {
                    const selected = answers[q.id] === oi;
                    let state = "idle";
                    if (result) {
                      if (oi === r.correctIndex) state = "correct";
                      else if (selected && oi === r.selectedIndex) state = "wrong";
                    } else if (selected) state = "selected";

                    const cls =
                      state === "correct"
                        ? "border-gold/60 bg-gold/10 text-gold"
                        : state === "wrong"
                        ? "border-destructive/60 bg-destructive/10 text-foreground"
                        : state === "selected"
                        ? "border-gold/50 bg-gold/10"
                        : "border-white/10 hover:bg-white/5";

                    return (
                      <button
                        key={oi}
                        disabled={!!result}
                        onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                        data-testid={`quiz-${qi}-option-${oi}`}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${cls}`}
                      >
                        <span className={`flex-shrink-0 h-5 w-5 rounded-full border flex items-center justify-center text-xs ${selected || (result && oi === r.correctIndex) ? "border-current" : "border-white/30"}`}>
                          {result && oi === r.correctIndex ? <CheckCircle2 className="h-4 w-4" /> : result && state === "wrong" ? <XCircle className="h-4 w-4 text-destructive" /> : String.fromCharCode(65 + oi)}
                        </span>
                        <span className="text-sm">{opt}</span>
                      </button>
                    );
                  })}
                </div>
                {result && (
                  <div className="mt-4 sm:ml-10 rounded-xl bg-white/5 border border-white/10 p-4">
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      <span className={`font-semibold ${r.correct ? "text-gold" : "text-amber-glow"}`}>{r.correct ? "Richtig. " : "Erklärung: "}</span>
                      {r.explanation}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="mt-8 flex items-center justify-between gap-4">
          {!result ? (
            <button
              onClick={submit}
              disabled={!allAnswered || submitting}
              data-testid="submit-quiz-button"
              className="ml-auto inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-50"
            >
              {submitting ? "Wird ausgewertet…" : allAnswered ? "Auswerten" : `Noch ${m.quiz.length - Object.keys(answers).length} offen`}
            </button>
          ) : (
            <>
              <button onClick={retry} data-testid="retry-quiz-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full glass hover:glow-gold transition-all">
                <RotateCcw className="h-4 w-4" /> Wiederholen
              </button>
              <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all">
                Zum Kurs <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
